import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NotInterested as BlockIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteEntry, markEntryInvalid } from "../features/admin/adminSlice";
import adminService from "../features/admin/adminService";
import { useAccessControl } from "../hooks/useAccessControl";

const AdminEntryActions = ({ entry, onActionComplete }) => {
  const { isAdmin } = useAccessControl();
  const dispatch = useDispatch();
  const { isLoading, isError, message } = useSelector((state) => state.admin);

  const [anchorEl, setAnchorEl] = useState(null);
  const [dialog, setDialog] = useState({ open: false, type: null });
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [breakdown, setBreakdown] = useState({
    open: false,
    loading: false,
    error: null,
    data: null,
  });

  if (!isAdmin) {
    return null;
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openDialog = (type) => {
    setDialog({ open: true, type });
    handleMenuClose();
    setError("");
    setReason("");
  };

  const openBreakdown = async () => {
    handleMenuClose();
    setBreakdown({ open: true, loading: true, error: null, data: null });

    try {
      const response = await adminService.getEntryPointsBreakdown(entry.id);
      setBreakdown({
        open: true,
        loading: false,
        error: null,
        data: response.data || response,
      });
    } catch (err) {
      setBreakdown({
        open: true,
        loading: false,
        error:
          err.response?.data?.message || err.message || "Failed to load breakdown",
        data: null,
      });
    }
  };

  const closeBreakdown = () => {
    setBreakdown({ open: false, loading: false, error: null, data: null });
  };

  const closeDialog = () => {
    setDialog({ open: false, type: null });
    setError("");
    setReason("");
  };

  const handleDelete = async () => {
    const result = await dispatch(deleteEntry(entry.id));
    if (result.type === deleteEntry.fulfilled.type) {
      closeDialog();
      onActionComplete?.("deleted");
    } else {
      setError(result.payload || "Failed to delete entry");
    }
  };

  const handleMarkInvalid = async () => {
    const result = await dispatch(
      markEntryInvalid({ entryId: entry.id, reason })
    );
    if (result.type === markEntryInvalid.fulfilled.type) {
      closeDialog();
      onActionComplete?.("marked_invalid");
    } else {
      setError(result.payload || "Failed to mark entry as invalid");
    }
  };

  const getDialogConfig = () => {
    switch (dialog.type) {
      case "delete":
        return {
          title: "Delete Entry",
          content:
            "Are you sure you want to delete this entry? This action cannot be undone.",
          action: handleDelete,
          actionLabel: "Delete",
          color: "error",
        };
      case "invalid":
        return {
          title: "Mark Entry as Invalid",
          content: "Enter a reason for marking this entry as invalid:",
          action: handleMarkInvalid,
          actionLabel: "Mark Invalid",
          color: "warning",
          showReasonField: true,
        };
      case "edit":
        return {
          title: "Edit Entry",
          content: "Edit functionality coming soon.",
          action: null,
          actionLabel: "Edit",
          color: "primary",
        };
      default:
        return null;
    }
  };

  const config = getDialogConfig();

  return (
    <>
      <IconButton size="small" onClick={handleMenuOpen} title="Admin Actions">
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => openDialog("edit")}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => openDialog("invalid")}>
          <BlockIcon fontSize="small" sx={{ mr: 1 }} />
          Mark Invalid
        </MenuItem>
        <MenuItem onClick={openBreakdown}>
          <CalculateIcon fontSize="small" sx={{ mr: 1 }} />
          Points Breakdown
        </MenuItem>
        <MenuItem
          onClick={() => openDialog("delete")}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Points calculation path */}
      <Dialog
        open={breakdown.open}
        onClose={closeBreakdown}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Points Breakdown</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {breakdown.loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {breakdown.error && (
            <Typography variant="body2" color="error">
              {breakdown.error}
            </Typography>
          )}

          {breakdown.data && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {breakdown.data.event} ·{" "}
                {breakdown.data.event_date
                  ? new Date(breakdown.data.event_date).toLocaleDateString(
                      "en-GB",
                    )
                  : "no date"}{" "}
                · current points:{" "}
                <strong>{breakdown.data.points}</strong>
              </Typography>

              {breakdown.data.breakdown?.breakdown?.length > 0 ? (
                <>
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Base points:{" "}
                      {breakdown.data.breakdown.basePoints ?? "—"}
                    </Typography>
                    {breakdown.data.breakdown.breakdown.map(
                      (step, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                          }}
                        >
                          <Typography variant="body2">
                            {index > 0 ? "↓ " : ""}
                            {step.name || step.step}{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              ({step.allocation}%)
                            </Typography>
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {step.points}
                          </Typography>
                        </Box>
                      ),
                    )}
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Total points
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {breakdown.data.breakdown.totalPoints ??
                        breakdown.data.points}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Shown from the system allocations at the time the points
                    were calculated. Organisation pages may apply their own
                    overrides.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No calculation breakdown recorded for this entry.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBreakdown}>Close</Button>
        </DialogActions>
      </Dialog>

      {config && (
        <Dialog
          open={dialog.open}
          onClose={closeDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{config.title}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {config.content}
            {config.showReasonField && (
              <TextField
                autoFocus
                fullWidth
                label="Reason"
                multiline
                rows={3}
                margin="normal"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this entry invalid?"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            {config.action && (
              <Button
                onClick={config.action}
                variant="contained"
                color={config.color}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  config.actionLabel
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AdminEntryActions;
